import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { isPirateTakeoverByTitle } from '../../utils/eventUtils';

/* ─── Section shell ─────────────────────────────────────────────────────────── */

const WhatsOnSection = styled.section.attrs(({ $darkMode }) => ({
  className: $darkMode ? 'bf-surface' : undefined
}))`
  margin: 0;
  padding: clamp(2.75rem, 6vw, 4.25rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid
    ${({ $darkMode }) => ($darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(16, 29, 64, 0.07)')};
  background: ${({ theme, $darkMode }) =>
    $darkMode
      ? 'rgba(5, 5, 8, 0.9)'
      : `linear-gradient(180deg, ${theme.colors.offWhite} 0%, rgba(241,239,234,0.92) 45%, ${theme.colors.offWhite} 100%)`};
`;

const WhatsOnInner = styled.div`
  max-width: min(1580px, 100%);
  margin: 0 auto;
`;

/* ─── Section header ─────────────────────────────────────────────────────────── */

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: clamp(2rem, 4.5vw, 3rem);
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: clamp(1rem, 2.5vw, 1.35rem);
`;

const HeaderCtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${({ $darkMode, theme }) => ($darkMode ? '#fff3d6' : theme.colors.accent)};
  padding: 0.65rem 1.25rem;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $darkMode, theme }) => ($darkMode ? 'rgba(255, 243, 214, 0.45)' : theme.colors.accent)};
  background: ${({ $darkMode }) =>
    $darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.92)'};
  box-shadow: 0 4px 18px rgba(16, 29, 64, 0.08);
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-1px);
  }

  &::after {
    content: '→';
    font-size: 1em;
    opacity: 0.9;
  }
`;

const SectionEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ $darkMode }) =>
    $darkMode ? 'rgba(255,255,255,0.42)' : 'rgba(16, 29, 64, 0.45)'};
  margin: 0 0 0.6rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.85rem, 3.8vw, 2.5rem);
  font-weight: 700;
  line-height: 1.12;
  color: ${({ $darkMode, theme }) => ($darkMode ? '#fff3d6' : theme.colors.primary)};
  margin: 0;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1rem auto 0;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
    opacity: ${({ $darkMode }) => ($darkMode ? 0.35 : 0.85)};
  }

  @media (max-width: 768px) {
    font-size: clamp(1.65rem, 5vw, 2rem);
  }
`;

/* ─── Grid & scroll ─────────────────────────────────────────────────────────── */

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(1rem, 2.5vw, 1.5rem);

  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px)  { grid-template-columns: 1fr; }
`;

/* Carousel: full-width track; arrows float on sides so cards align with sections below */
const CarouselShell = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 540px) {
    display: block;
  }
`;

const TrackOuter = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 540px) {
    overflow: visible;
  }
`;

const ScrollContent = styled.div`
  display: flex;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;

  @media (max-width: 540px) {
    display: block;
    transform: none !important;
  }
`;

const EventGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(1rem, 2.5vw, 1.5rem);
  flex: 0 0 100%;

  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px)  { grid-template-columns: 1fr; }
`;

/* ─── Nav arrows ─────────────────────────────────────────────────────────── */

const NavBtn = styled.button`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: ${props => props.theme.colors.accent};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.28) inset,
    0 6px 22px rgba(157, 109, 73, 0.38);
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.94;
    transform: scale(1.06);
  }

  &:disabled {
    opacity: 0.32;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CarouselNavBtn = styled(NavBtn)`
  position: absolute;
  top: 50%;
  z-index: 4;
  transform: translateY(-50%);
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.28) inset,
    0 8px 28px rgba(16, 29, 64, 0.18),
    0 6px 22px rgba(157, 109, 73, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.06);
  }

  &:disabled {
    transform: translateY(-50%);
  }

  @media (max-width: 540px) {
    display: none;
  }
`;

const CarouselNavPrev = styled(CarouselNavBtn)`
  left: clamp(0.15rem, 1.25vw, 0.65rem);
`;

const CarouselNavNext = styled(CarouselNavBtn)`
  right: clamp(0.15rem, 1.25vw, 0.65rem);
`;

/* ─── Card ───────────────────────────────────────────────────────────────── */

const EventCard = styled.article.attrs(({ $darkMode, $isPirate }) => ({
  className: $darkMode && !$isPirate ? 'bf-surface' : undefined
}))`
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid
    ${({ $isPirate, $darkMode }) => {
      if ($isPirate) return 'rgba(26, 58, 82, 0.28)';
      return $darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(16, 29, 64, 0.1)';
    }};
  background: ${({ $isPirate, $darkMode }) => {
    if ($isPirate && !$darkMode) return 'rgba(255, 255, 255, 0.74)';
    if ($darkMode) return 'rgba(18, 18, 24, 0.95)';
    return 'rgba(255, 255, 255, 0.68)';
  }};
  backdrop-filter: ${({ $darkMode }) => ($darkMode ? 'none' : 'blur(16px)')};
  -webkit-backdrop-filter: ${({ $darkMode }) => ($darkMode ? 'none' : 'blur(16px)')};
  box-shadow: ${({ $isPirate, $darkMode }) => {
    if ($isPirate && !$darkMode) {
      return `0 8px 36px rgba(26, 58, 82, 0.1),
        0 1px 0 rgba(255, 255, 255, 0.88) inset,
        inset 0 0 0 1px rgba(244, 208, 63, 0.22)`;
    }
    if ($darkMode) return '0 8px 32px rgba(0,0,0,0.25)';
    return '0 8px 36px rgba(16,29,64,0.09), 0 1px 0 rgba(255,255,255,0.82) inset';
  }};
  transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $isPirate, $darkMode }) => {
      if ($isPirate) return 'rgba(157, 109, 73, 0.35)';
      return $darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(157,109,73,0.3)';
    }};
    box-shadow: ${({ $isPirate, $darkMode }) => {
      if ($isPirate && !$darkMode) {
        return `0 14px 44px rgba(26, 58, 82, 0.12),
          0 1px 0 rgba(255, 255, 255, 0.92) inset,
          inset 0 0 0 1px rgba(244, 208, 63, 0.35)`;
      }
      if ($darkMode) return '0 14px 44px rgba(0,0,0,0.32)';
      return '0 16px 50px rgba(16,29,64,0.11), 0 1px 0 rgba(255,255,255,0.88) inset';
    }};
  }
`;

/* Image fills top of card; badges float over it */
const CardImageWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;

  ${({ $isPirate }) =>
    $isPirate &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 3px;
      background: linear-gradient(90deg, transparent, rgba(244, 208, 63, 0.65), transparent);
      pointer-events: none;
    }
  `}
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: 200px;
  object-fit: cover;
  pointer-events: none;
  user-select: none;

  @media (max-width: 540px) {
    height: 260px;
  }
`;

/* Subtle gradient scrim so badges always read over any photo */
const ImageScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(10, 14, 30, 0.38) 0%,
    transparent 50%
  );
  pointer-events: none;
`;

const BadgeRow = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.85rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${({ $stack }) => ($stack ? '0.45rem' : '0.35rem')};
  max-width: calc(100% - 1.7rem);
  flex-direction: ${({ $stack }) => ($stack ? 'column' : 'row')};
`;

const LocationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $darkMode, theme }) =>
    $darkMode ? 'rgba(247,198,84,0.85)' : theme.colors.accent};
  color: ${({ $darkMode }) => ($darkMode ? '#1a1200' : '#fff')};
  border: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.18)'};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const PirateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.7rem 0.28rem 0.55rem;
  border-radius: 999px;
  background: rgba(15, 28, 42, 0.82);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #fde68a;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  border: 1px solid rgba(253, 230, 138, 0.35);
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.22);

  span[aria-hidden='true'] {
    font-size: 0.85em;
    line-height: 1;
    opacity: 0.95;
  }
`;

const FlagshipBadge = styled(LocationBadge)`
  background: rgba(255,255,255,0.92);
  color: ${props => props.theme.colors.primary};
  border-color: rgba(255,255,255,0.4);
`;

const IconBadge = styled(FlagshipBadge)``;

/* Card body */
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1.1rem 1.2rem 1.25rem;
  gap: 0.55rem;
  border-top: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(16,29,64,0.07)'};
`;

const EventDate = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $darkMode, theme }) =>
    $darkMode ? 'rgba(247,198,84,0.82)' : theme.colors.accent};
`;

const EventTitle = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  font-weight: 700;
  line-height: 1.2;
  color: ${({ $darkMode, theme }) => ($darkMode ? '#fff3d6' : theme.colors.primary)};
  cursor: pointer;
`;

const EventDescription = styled.p`
  margin: 0;
  flex: 1;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${({ $darkMode }) =>
    $darkMode ? 'rgba(248,246,240,0.72)' : 'rgba(16,29,64,0.6)'};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.35rem;
  border-top: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(16,29,64,0.07)'};
`;

const ReadMoreText = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $darkMode, theme }) =>
    $darkMode ? 'rgba(247,198,84,0.85)' : theme.colors.accent};
`;

const ArrowChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.85rem 0.35rem 0.65rem;
  border-radius: 999px;
  background: ${({ $darkMode, theme }) =>
    $darkMode
      ? 'rgba(247,198,84,0.14)'
      : theme.colors.accent};
  border: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(247,198,84,0.28)' : 'transparent'};
  color: ${({ $darkMode }) => ($darkMode ? '#f7c654' : '#fff')};
  box-shadow: ${({ $darkMode }) =>
    $darkMode ? 'none' : '0 2px 10px rgba(157,109,73,0.3)'};
  transition: opacity 0.2s ease, transform 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    fill: currentColor;
  }

  ${EventCard}:hover & {
    opacity: 0.92;
    transform: translateX(2px);
  }
`;

/* Empty / error states */

const EmptyMessage = styled.p`
  text-align: center;
  margin: 0 auto;
  max-width: 36rem;
  padding: clamp(1.5rem, 4vw, 2rem);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  line-height: 1.55;
  color: ${({ $darkMode }) =>
    $darkMode ? 'rgba(248,246,240,0.7)' : 'rgba(16,29,64,0.62)'};
  background: ${({ $darkMode }) =>
    $darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)'};
  border: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(16,29,64,0.1)'};
  border-radius: 16px;
  backdrop-filter: ${({ $darkMode }) => ($darkMode ? 'blur(8px)' : 'blur(14px)')};
  -webkit-backdrop-filter: ${({ $darkMode }) => ($darkMode ? 'blur(8px)' : 'blur(14px)')};
`;

/* ─── Date formatter ─────────────────────────────────────────────────────────── */

const formatEventDate = dateStr => {
  if (!dateStr) return '';

  if (dateStr.includes(' to ')) {
    const [startDate, endDate] = dateStr.split(' to ');
    const startParts  = startDate.split(', ');
    const startPieces = startParts[1].split(' ');
    const startDay    = startPieces[1];
    const startMonth  = startPieces[0];
    const endParts    = endDate.split(', ');
    const endPieces   = endParts[1].split(' ');
    const endDay      = endPieces[1];
    const endMonth    = endPieces[0];
    const year        = endParts[2];
    if (startMonth === endMonth) return `${startDay}–${endDay} ${startMonth} ${year}`;
    return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
  }

  const parts = dateStr.split(', ');
  if (parts.length >= 2) {
    const dm = parts[1].split(' ');
    return `${dm[1]} ${dm[0]} ${parts[2]}`;
  }
  return dateStr;
};

/* ─── Card renderer (extracted to avoid triple duplication) ──────────────────── */

const renderCard = (event, index, darkMode, handleEventClick) => {
  const isPirate = event.isPirateTakeover || isPirateTakeoverByTitle(event.title);
  const dm = darkMode;

  return (
    <EventCard
      key={index}
      $darkMode={dm}
      $isPirate={isPirate}
      onClick={() => handleEventClick(event)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleEventClick(event)}
    >
      <CardImageWrap $isPirate={isPirate}>
        <CardImage src={event.image} alt={event.title} draggable={false} />
        <ImageScrim />
        <BadgeRow $stack={isPirate}>
          <LocationBadge $darkMode={dm}>{event.location}</LocationBadge>
          {isPirate && (
            <PirateBadge>
              <span aria-hidden="true">⚓</span>
              Pirate takeover
            </PirateBadge>
          )}
          {event.isFlagship && !isPirate && (
            <FlagshipBadge>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{width:10,height:10,marginRight:2}}>
                <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
              </svg>
              Flagship
            </FlagshipBadge>
          )}
          {event.isEarlyBird && <IconBadge>Early Bird</IconBadge>}
          {event.isDogFriendly && <IconBadge>🐾 Dogs</IconBadge>}
          {event.isAccessible && <IconBadge>♿ Accessible</IconBadge>}
        </BadgeRow>
      </CardImageWrap>

      <CardBody $darkMode={dm}>
        {event.date && (
          <EventDate $darkMode={dm}>{formatEventDate(event.date)}</EventDate>
        )}
        <EventTitle
          $darkMode={dm}
          onClick={e => { e.stopPropagation(); handleEventClick(event); }}
        >
          {event.title}
        </EventTitle>
        {event.description && (
          <EventDescription $darkMode={dm}>{event.description}</EventDescription>
        )}
        <CardFooter $darkMode={dm}>
          <ReadMoreText $darkMode={dm}>{event.linkText || 'Find out more'}</ReadMoreText>
          <ArrowChip
            $darkMode={dm}
            onClick={e => { e.stopPropagation(); handleEventClick(event); }}
            role="button"
            aria-label={`View ${event.title}`}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
          </ArrowChip>
        </CardFooter>
      </CardBody>
    </EventCard>
  );
};

/* ─── Component ─────────────────────────────────────────────────────────────── */

const WhatsOn = ({
  title,
  events,
  emptyMessage,
  showParklandSection = true,
  enableScrolling = false,
  darkMode = false,
  headerEyebrow,
  headerCta,
}) => {
  const navigate = useNavigate();
  const [mainPage, setMainPage]     = React.useState(0);
  const [parkPage, setParkPage]     = React.useState(0);
  const [perPage, setPerPage]       = React.useState(4);

  React.useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setPerPage(w >= 1200 ? 4 : w >= 900 ? 3 : w >= 600 ? 2 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleEventClick = event => {
    if (event?.link) { window.location.assign(event.link); return; }
    if (event?.id)   { navigate(`/event/${event.id}`);     return; }
  };

  const parklandEvents = showParklandSection
    ? events.filter(e => e.location?.toLowerCase() === 'parkland')
    : [];
  const otherEvents = events.filter(e => e.location?.toLowerCase() !== 'parkland');

  const renderSection = (eventsToRender, isParkland) => {
    /* static grid (no-scroll mode or mobile) */
    if (!enableScrolling || window.innerWidth <= 540) {
      const list = enableScrolling
        ? eventsToRender
        : eventsToRender.slice(0, 4);
      return (
        <CardGrid>
          {list.map((ev, i) => renderCard(ev, i, darkMode, handleEventClick))}
        </CardGrid>
      );
    }

    /* paginated scroll */
    const groups = [];
    for (let i = 0; i < eventsToRender.length; i += perPage) {
      groups.push(eventsToRender.slice(i, i + perPage));
    }
    const currentPage = isParkland ? parkPage : mainPage;

    return (
      <CarouselShell>
        <TrackOuter>
          <ScrollContent style={{ transform: `translateX(-${currentPage * 100}%)` }}>
            {groups.map((group, gi) => (
              <EventGroup key={gi}>
                {group.map((ev, i) => renderCard(ev, i, darkMode, handleEventClick))}
              </EventGroup>
            ))}
          </ScrollContent>
        </TrackOuter>
        {groups.length > 1 && (
          <>
            <CarouselNavPrev
              type="button"
              onClick={() =>
                isParkland
                  ? setParkPage(p => Math.max(0, p - 1))
                  : setMainPage(p => Math.max(0, p - 1))
              }
              disabled={currentPage === 0}
              aria-label="Previous events"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </CarouselNavPrev>
            <CarouselNavNext
              type="button"
              onClick={() =>
                isParkland
                  ? setParkPage(p => Math.min(groups.length - 1, p + 1))
                  : setMainPage(p => Math.min(groups.length - 1, p + 1))
              }
              disabled={currentPage >= groups.length - 1}
              aria-label="Next events"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </CarouselNavNext>
          </>
        )}
      </CarouselShell>
    );
  };

  return (
    <>
      <WhatsOnSection $darkMode={darkMode}>
        <WhatsOnInner>
          <HeaderBlock>
            <SectionEyebrow $darkMode={darkMode}>
              {headerEyebrow ?? 'Events & Activities'}
            </SectionEyebrow>
            <SectionTitle $darkMode={darkMode}>{title}</SectionTitle>
            {headerCta?.to && headerCta?.label && (
              <HeaderActions>
                <HeaderCtaLink $darkMode={darkMode} to={headerCta.to}>
                  {headerCta.label}
                </HeaderCtaLink>
              </HeaderActions>
            )}
          </HeaderBlock>
          {otherEvents.length > 0
            ? renderSection(otherEvents, false)
            : <EmptyMessage $darkMode={darkMode}>{emptyMessage || 'No events currently scheduled. Check back soon!'}</EmptyMessage>}
        </WhatsOnInner>
      </WhatsOnSection>

      {showParklandSection && parklandEvents.length > 0 && (
        <WhatsOnSection $darkMode={darkMode}>
          <WhatsOnInner>
            <HeaderBlock>
              <SectionEyebrow $darkMode={darkMode}>Parkland</SectionEyebrow>
              <SectionTitle $darkMode={darkMode}>What&apos;s On in the Parkland</SectionTitle>
            </HeaderBlock>
            {renderSection(parklandEvents, true)}
          </WhatsOnInner>
        </WhatsOnSection>
      )}
    </>
  );
};

WhatsOn.propTypes = {
  title:              PropTypes.string.isRequired,
  events:             PropTypes.array.isRequired,
  emptyMessage:       PropTypes.string,
  showParklandSection: PropTypes.bool,
  enableScrolling:    PropTypes.bool,
  darkMode:           PropTypes.bool,
  headerEyebrow:      PropTypes.string,
  headerCta:          PropTypes.shape({
    to: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
};

export default WhatsOn;
